// Catalog.ID Client — Leaving a session from the unlock screen
// ============================================================================
// Pure helpers shared by the login view and the app's boot path. A stored
// session token is not proof of a live session: the server may have revoked
// it (logout elsewhere, lockdown), it may have expired, or a stale localStorage
// snapshot may have resurrected it. When the device-unlock step learns that,
// the app must return to the login form rather than dead-end on a supersecret
// prompt whose every call would fail the same way.

export interface SessionLostInfo {
    /** The identity whose session ended, when known. */
    cid: string | null;
    /**
     * `rejected`: the server refused the stored token (expired, revoked,
     * unknown) — the token has been forgotten. `switch`: the user chose to log
     * in as somebody else from the unlock screen — the stored token is kept so
     * the identity picker can still switch back to it, only the active identity
     * is cleared.
     */
    reason: 'rejected' | 'switch';
}

export interface LoginViewOptions {
    /** Username to prefill. Absent: the active identity, if any, else blank. */
    prefillCid?: string;
    /** A one-line notice shown above the form (not an error). */
    notice?: string;
    /**
     * The typed identity lives at another node (its origin lookup found a URL):
     * the host takes over and NOTHING is sent — no login, no password. Only the
     * browser OIDC consent route supplies it: it forwards the whole
     * authorization to that origin (docs/cid-governance.md M7b). Absent, the
     * form signs in at the origin as usual (M7a).
     */
    onHomeNodeElsewhere?: (cid: string, origin: { code?: string; url: string }) => void;
}

/** What the login form should show after the unlock screen gave up a session. */
export function loginOptionsAfterSessionLoss(info: SessionLostInfo): LoginViewOptions {
    if (info.reason !== 'rejected') return {};
    const who = info.cid ? `Your session for ${info.cid}` : 'Your session';
    return {
        prefillCid: info.cid ?? undefined,
        notice: `${who} has expired or was ended. Log in again to continue.`,
    };
}

/** The unlock screen's account line — the user must know WHOSE secrets are being asked for. */
export function unlockAccountLine(cid: string): string {
    return `Account: ${cid}`;
}
